import React, { useContext, useState, useRef } from 'react'
import './css/reset-password.css'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'
import { AppContext } from '../context/appContext'
import {
    Box,
    Button,
    CircularProgress,
    TextField,
    InputAdornment,
    IconButton,
    Typography,
    Paper,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { Visibility, VisibilityOff } from '@mui/icons-material';

const ResetPassword = () => {
    const { backendUrl } = useContext(AppContext);
    const navigate = useNavigate();

    const [isEmailSent, setIsEmailSent] = useState(false);
    const [isOtpSubmitted, setIsOtpSubmitted] = useState(false);
    const [otp, setOtp] = useState('');
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const inputRefs = useRef([]);

    const {
        register,
        handleSubmit,
        setError,
        clearErrors,
        formState: { errors },
        watch,
    } = useForm();

    const passwordValue = watch('password');

    const handleChange = (e, index) => {
        const value = e.target.value;

        if (index === 0 && value.length === 6) {
            value.split('').forEach((ch, i) => {
                if (inputRefs.current[i]) {
                    inputRefs.current[i].value = ch;
                }
            });
            inputRefs.current[5].focus();
            return;
        }

        if (value && index < 5) {
            inputRefs.current[index + 1].focus();
        }
    };

    const handleKeyDown = (e, index) => {
        if (e.key === 'Backspace' && e.target.value === '' && index > 0) {
            inputRefs.current[index - 1].focus();
        }
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const pasted = e.clipboardData.getData("text").slice(0, 6).split("");

        pasted.forEach((char, i) => {
            if (inputRefs.current[i]) inputRefs.current[i].value = char;
        });

        inputRefs.current[5].focus();
    };

    const onSubmitEmail = async (data) => {
        setLoading(true);
        try {
            const res = await axios.post(`${backendUrl}/api/auth/send-reset-otp`, {
                email: data.email,
            });
            if (res.data.success) {
                setIsEmailSent(true);
            } else {
                setError('email', { message: res.data.message });
            }
        } catch (err) {
            setError('email', { message: 'Server error, try again later.' });
        } finally {
            setLoading(false);
        }
    };

    const onSubmitOtp = async () => {
        const otpValue = inputRefs.current.map((i) => i.value).join('');
        if (otpValue.length !== 6) {
            setError('otp', { message: 'Please enter all 6 digits.' });
            return;
        }
        clearErrors('otp');
        setOtp(otpValue);
        setIsOtpSubmitted(true);
    };

    const handleTogglePassword = () => setShowPassword((prev) => !prev);
    const handleToggleConfirmPassword = () => setShowConfirmPassword((prev) => !prev);

    const onSubmitPassword = async (data) => {
        setLoading(true);
        try {
            const res = await axios.post(`${backendUrl}/api/auth/reset-password`, {
                email: data.email,
                otp,
                password: data.password,
            });

            if (res.data.success) {
                navigate('/login');
            } else {
                setError('password', { message: res.data.message });
            }
        } catch (err) {
            setError('password', { message: 'Failed to reset password.' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box className="reset-page">
            <Paper elevation={10} className="reset-container">
                {!isEmailSent && (
                    <form onSubmit={handleSubmit(onSubmitEmail)}>
                        <Typography variant="h4" className="reset-title">
                            Reset Password
                        </Typography>
                        <Typography className="reset-subtitle">
                            Enter your registered email
                        </Typography>

                        <TextField
                            fullWidth
                            label="Email Address"
                            variant="outlined"
                            {...register('email', {
                                required: 'Email is required',
                                pattern: {
                                    value: /^\S+@\S+$/i,
                                    message: 'Invalid email address',
                                },
                            })}
                            error={!!errors.email}
                            helperText={errors.email?.message}
                            className="reset-field"
                        />

                        <Button
                            type="submit"
                            variant="contained"
                            fullWidth
                            className="reset-btn"
                            disabled={loading}
                        >
                            {loading ? <CircularProgress size={22} color="inherit" /> : 'Submit'}
                        </Button>
                    </form>
                )}

                {isEmailSent && !isOtpSubmitted && (
                    <form onSubmit={onSubmitOtp}>
                        <Typography variant="h4" className="reset-title">
                            Verify OTP
                        </Typography>
                        <Typography className="reset-subtitle">
                            Enter the 6-digit code sent to your email
                        </Typography>

                        <Box className="otp-group" onPaste={handlePaste}>
                            {Array(6).fill('').map((_, i) => (
                                <input
                                    key={i}
                                    type="text"
                                    maxLength="1"
                                    inputMode="numeric"
                                    autoComplete="one-time-code"
                                    ref={(el) => (inputRefs.current[i] = el)}
                                    onChange={(e) => handleChange(e, i)}
                                    onKeyDown={(e) => handleKeyDown(e, i)}
                                    className="otp-input"
                                />
                            ))}
                        </Box>

                        {errors.otp && (
                            <Typography className="error-text">{errors.otp.message}</Typography>
                        )}

                        <Button
                            type="submit"
                            variant="contained"
                            fullWidth
                            className="reset-btn"
                        >
                            Continue
                        </Button>
                    </form>
                )}

                {isOtpSubmitted && (
                    <form onSubmit={handleSubmit(onSubmitPassword)}>
                        <Typography variant="h4" className="reset-title">
                            Set New Password
                        </Typography>
                        <Typography className="reset-subtitle">
                            Create your new secure password
                        </Typography>

                        <TextField
                            fullWidth
                            label="New Password"
                            variant="outlined"
                            type={showPassword ? 'text' : 'password'}
                            {...register('password', {
                                required: 'Password is required',
                                minLength: { value: 6, message: 'Minimum 6 characters required' },
                            })}
                            error={!!errors.password}
                            helperText={errors.password?.message}
                            className="reset-field"
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton onClick={handleTogglePassword} edge="end" sx={{ color: '#9ca3af' }}>
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />

                        <TextField
                            fullWidth
                            label="Confirm Password"
                            variant="outlined"
                            type={showConfirmPassword ? 'text' : 'password'}
                            {...register('confirmPassword', {
                                required: 'Please confirm your password',
                                validate: (value) =>
                                    value === passwordValue || 'Passwords do not match',
                            })}
                            error={!!errors.confirmPassword}
                            helperText={errors.confirmPassword?.message}
                            className="reset-field"
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton onClick={handleToggleConfirmPassword} edge="end" sx={{ color: '#9ca3af' }}>
                                            {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />

                        <Button
                            type="submit"
                            variant="contained"
                            fullWidth
                            className="reset-btn"
                            disabled={loading}
                        >
                            {loading ? <CircularProgress size={22} color="inherit" /> : 'Reset Password'}
                        </Button>
                    </form>
                )}
            </Paper>
        </Box>
    );
};

export default ResetPassword;